// src/components/SecureMedia/SecureMediaPlayer.tsx
import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';

interface SecureMediaPlayerProps {
  secureVideoUrl?: string;
  poster?: string;
  className?: string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  onTimeUpdate?: (currentTime: number) => void;
  onLoadedData?: () => void;
  onError?: () => void;
  disableRightClick?: boolean;
  disableDownload?: boolean;
  watermarkText?: string;
  allowPublicViewing?: boolean; // New prop for public live video viewing
}

const SecureMediaPlayer: React.FC<SecureMediaPlayerProps> = ({
  secureVideoUrl,
  poster,
  className = '',
  controls = true,
  autoPlay = false,
  muted = false,
  loop = false,
  onTimeUpdate,
  onLoadedData,
  onError,
  disableRightClick = true,
  disableDownload = true,
  watermarkText,
  allowPublicViewing = false, // Default to secure mode
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [lastProgressTime, setLastProgressTime] = useState(0);
  const [stallCheckInterval, setStallCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  // Disable right-click and other download methods
  useEffect(() => {
    if (!disableRightClick) return;

    const handleContextMenu = (e: Event) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Disable Ctrl+S, Ctrl+Shift+S, F12, Ctrl+U, Ctrl+Shift+I, etc.
      if (
        (e.ctrlKey && e.key === 's') ||
        (e.ctrlKey && e.shiftKey && e.key === 'S') ||
        e.key === 'F12' ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'C')
      ) {
        e.preventDefault();
        return false;
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [disableRightClick]);

  // Handle URL refresh for token expiry without recreating the component
  useEffect(() => {
    if (!secureVideoUrl || !videoRef.current) return;

    // If the URL changes, update the video source without recreating the component
    if (videoRef.current.src !== secureVideoUrl) {
      const currentTime = videoRef.current.currentTime;
      const wasPaused = videoRef.current.paused;
      
      videoRef.current.src = secureVideoUrl;
      videoRef.current.load();
      
      // Try to maintain playback position
      if (currentTime > 0) {
        videoRef.current.addEventListener('loadeddata', function restorePosition() {
          if (videoRef.current) {
            videoRef.current.currentTime = currentTime;
            if (!wasPaused) {
              videoRef.current.play().catch(console.error);
            }
          }
          videoRef.current?.removeEventListener('loadeddata', restorePosition);
        });
      }
    }

    const urlParams = new URLSearchParams(secureVideoUrl.split('?')[1] || '');
    const expires = urlParams.get('expires');
    
    if (expires) {
      const expiryTime = parseInt(expires) * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const refreshTime = expiryTime - currentTime - 300000; // Refresh 5 minutes before expiry
      
      if (refreshTime > 0) {
        const refreshTimeout = setTimeout(() => {
          // Trigger a re-fetch by calling onError
          onError?.();
        }, refreshTime);
        
        return () => clearTimeout(refreshTimeout);
      }
    }
  }, [secureVideoUrl, onError]);

  // Disable drag and drop
  useEffect(() => {
    if (!disableDownload) return;

    const handleDragStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    const handleSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    const video = videoRef.current;
    if (video) {
      video.addEventListener('dragstart', handleDragStart);
      video.addEventListener('selectstart', handleSelectStart);
      
      // Disable download attribute
      video.removeAttribute('download');
      video.controlsList.add('nodownload');
    }

    return () => {
      if (video) {
        video.removeEventListener('dragstart', handleDragStart);
        video.removeEventListener('selectstart', handleSelectStart);
      }
    };
  }, [disableDownload]);

  const handleLoadedData = () => {
    setIsLoading(false);
    onLoadedData?.();
  };

  const handleError = (e: any) => {
    setIsLoading(false);
    setHasError(true);
    console.error('Secure media player error:', {
      error: e,
      currentSrc: videoRef.current?.src,
      networkState: videoRef.current?.networkState,
      readyState: videoRef.current?.readyState,
      duration: videoRef.current?.duration,
      currentTime: videoRef.current?.currentTime
    });
    onError?.();
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && onTimeUpdate) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      // Debug: Video playback progress tracking
      onTimeUpdate(currentTime);
    }
  };

  const handleEnded = () => {
    // Video ended, restarting for loop...
    // Custom loop implementation instead of relying on HTML5 loop
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Video restarted successfully
          })
          .catch((error) => {
            console.error('Failed to restart video:', error);
            // If autoplay fails, try refreshing the URL
            setTimeout(() => {
              onError?.();
            }, 1000);
          });
      }
    }
  };

  const handleWaiting = () => {
    // Video waiting/buffering...
    setIsLoading(true);
  };

  const handleCanPlay = () => {
    // Video can play...
    setIsLoading(false);
  };

  const handleLoadStart = () => {
    // Video load started...
  };

  const handleDurationChange = () => {
    if (videoRef.current) {
      // Video duration received
    }
  };

  const handleProgress = () => {
    if (videoRef.current) {
      const buffered = videoRef.current.buffered;
      if (buffered.length > 0) {
        const _bufferedEnd = buffered.end(buffered.length - 1);
        const _duration = videoRef.current.duration;
        // Buffered progress tracking
      }
    }
  };

  const handleStalled = () => {
    console.warn('Video playback stalled');
  };

  const handleSuspend = () => {
    console.warn('Video loading suspended');
  };

  // Handle mute state changes without causing re-renders
  useEffect(() => {
    if (videoRef.current && videoRef.current.muted !== muted) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  // Authentication check for secure content
  if (!userInfo && !allowPublicViewing) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-800 text-white">
        <p>Please log in to watch this video</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-800 text-white">
        <p>Error loading video</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      )}

      {/* Watermark */}
      {watermarkText && (
        <div className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm pointer-events-none">
          {watermarkText}
        </div>
      )}

      {/* Video element */}
      {secureVideoUrl && (
        <video
          ref={videoRef}
          src={secureVideoUrl}
          poster={poster}
          controls={controls}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline
          preload="auto"
          crossOrigin="anonymous"
          onLoadStart={handleLoadStart}
          onLoadedData={handleLoadedData}
          onDurationChange={handleDurationChange}
          onProgress={handleProgress}
          onTimeUpdate={handleTimeUpdate}
          onWaiting={handleWaiting}
          onCanPlay={handleCanPlay}
          onEnded={handleEnded}
          onError={handleError}
          onStalled={handleStalled}
          onSuspend={handleSuspend}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          className="w-full h-full"
          style={{
            pointerEvents: disableRightClick ? 'none' : 'auto'
          }}
          onContextMenu={(e) => disableRightClick && e.preventDefault()}
          onSelect={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          // Prevent common screenshot shortcuts
          onKeyDown={(e) => {
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const isScreenshot = isMac 
              ? (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5'))
              : (e.key === 'PrintScreen' || (e.altKey && e.key === 'PrintScreen'));
            
            if (isScreenshot) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        >
          Your browser does not support the video tag.
        </video>
      )}

      {/* Overlay to prevent right-click (for controls) */}
      {disableRightClick && controls && (
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 1 }}
        />
      )}
    </div>
  );
};

export default SecureMediaPlayer;
